/**
 * Shared shapes for first-party web default events (pageleave and
 * element interactions). Kept free of React so unit tests and the
 * service-free acceptance harness can import them without Next.js.
 *
 * Interaction capture is allow-list only: the producer never reads input
 * values, keystrokes, or field content, and the catalog has no field that
 * could hold them (ENG-5005 / ENG-4810).
 */

export const PAGELEAVE_EVENT_NAME = "pageleave" as const;
export const ELEMENT_INTERACTION_EVENT_NAME = "element_interaction" as const;

export const INTERACTIVE_ELEMENT_TAGS = [
  "a",
  "button",
  "form",
  "input",
  "select",
  "textarea",
  "label",
] as const;
export type InteractiveElementTag = (typeof INTERACTIVE_ELEMENT_TAGS)[number];

export const INTERACTION_ACTIONS = ["click", "change", "submit"] as const;
export type InteractionAction = (typeof INTERACTION_ACTIONS)[number];

export const INTERACTION_ELEMENT_ID_MAX_BYTES = 128;
export const INTERACTION_HREF_PATH_MAX_BYTES = 255;
export const INTERACTION_INPUT_TYPE_MAX_BYTES = 32;

const INTERACTIVE_TAG_SET = new Set<string>(INTERACTIVE_ELEMENT_TAGS);

/** Click targets; `form` is submit-only so wrapping content is not a click. */
const CLICK_SELECTOR = "a,button,input,select,textarea,label";
const CHANGE_SELECTOR = "input,select,textarea";
const SUBMIT_SELECTOR = "form";

/** Keys that must never appear on an interaction event, by construction. */
export const FORBIDDEN_INTERACTION_PROPERTY_KEYS = [
  "value",
  "checked",
  "selected",
  "files",
  "text",
  "textContent",
  "innerText",
  "innerHTML",
  "outerHTML",
  "key",
  "code",
  "keyCode",
  "data",
  "defaultValue",
] as const;

export type PageleaveProperties = {
  pageview_id: string;
};

export type ElementInteractionProperties = {
  element: InteractiveElementTag;
  action: InteractionAction;
  element_id?: string;
  href_path?: string;
  input_type?: string;
};

/** Catalogued properties that pair a pageleave with its pageview event_id. */
export function pageleaveProperties(pageviewId: string): PageleaveProperties {
  return { pageview_id: pageviewId };
}

/**
 * Bounded interaction metadata from allow-listed attributes only. Callers
 * must not pass field content; this function has no parameter that could
 * accept a value, keystroke, or selected option.
 */
export function boundedInteractionProperties(source: {
  tagName: string;
  action: InteractionAction;
  id?: string | null;
  href?: string | null;
  type?: string | null;
}): ElementInteractionProperties | null {
  const element = source.tagName.toLowerCase();
  if (!isInteractiveElementTag(element)) return null;
  if (!actionMatchesElement(source.action, element)) return null;

  const properties: ElementInteractionProperties = {
    element,
    action: source.action,
  };
  const elementId = boundedAttribute(source.id, INTERACTION_ELEMENT_ID_MAX_BYTES);
  if (elementId) properties.element_id = elementId;
  if (element === "a") {
    const hrefPath = boundedHrefPath(source.href);
    if (hrefPath) properties.href_path = hrefPath;
  }
  if (element === "input" || element === "button") {
    const inputType = boundedAttribute(source.type, INTERACTION_INPUT_TYPE_MAX_BYTES);
    if (inputType) properties.input_type = inputType;
  }
  return properties;
}

/**
 * Resolve a click/change/submit Event to catalogued interaction properties.
 * Reads tagName and a short attribute allow-list only — never `.value`,
 * `.textContent`, selected options, or keyboard fields.
 */
export function elementInteractionFromEvent(event: Event): ElementInteractionProperties | null {
  const action = actionForEvent(event);
  if (!action) return null;
  const origin = eventTargetElement(event.target);
  if (!origin) return null;
  const element = origin.closest(selectorForAction(action));
  if (!element) return null;
  return boundedInteractionProperties({
    tagName: element.tagName,
    action,
    id: element.getAttribute("id"),
    href: element.getAttribute("href"),
    type: element.getAttribute("type"),
  });
}

export function isInteractiveElementTag(value: string): value is InteractiveElementTag {
  return INTERACTIVE_TAG_SET.has(value);
}

function actionForEvent(event: Event): InteractionAction | null {
  if (event.type === "click" || event.type === "change" || event.type === "submit") {
    return event.type;
  }
  return null;
}

function selectorForAction(action: InteractionAction): string {
  if (action === "submit") return SUBMIT_SELECTOR;
  if (action === "change") return CHANGE_SELECTOR;
  return CLICK_SELECTOR;
}

function actionMatchesElement(action: InteractionAction, element: InteractiveElementTag): boolean {
  if (action === "submit") return element === "form";
  if (action === "change") {
    return element === "input" || element === "select" || element === "textarea";
  }
  return element !== "form";
}

function eventTargetElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

function boundedAttribute(value: string | null | undefined, maxBytes: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (utf8Bytes(trimmed) > maxBytes) return undefined;
  return trimmed;
}

function boundedHrefPath(href: string | null | undefined): string | undefined {
  const trimmed = typeof href === "string" ? href.trim() : "";
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith("javascript:") ||
    lower.startsWith("data:") ||
    lower.startsWith("vbscript:") ||
    lower.startsWith("mailto:") ||
    lower.startsWith("tel:") ||
    lower.startsWith("sms:")
  ) {
    return undefined;
  }
  try {
    const url = new URL(trimmed, "https://axp.invalid");
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    return boundedAttribute(url.pathname || "/", INTERACTION_HREF_PATH_MAX_BYTES);
  } catch {
    return undefined;
  }
}

function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}
